import { Navigate, Outlet } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { roleHomePath } from '@/lib/auth-utils';
import type { UserRole } from '@/lib/types';

type ProtectedRouteProps = {
  allowedRoles?: Exclude<UserRole, null>[];
};

export function createProtectedLayout(allowedRoles?: Exclude<UserRole, null>[]) {
  return function ProtectedLayout() {
    return <ProtectedRoute allowedRoles={allowedRoles} />;
  };
}

export const AdminLayout = createProtectedLayout(['admin']);
export const DoctorLayout = createProtectedLayout(['doctor']);

export function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && user.role && !allowedRoles.includes(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  return <Outlet />;
}

export function GuestRoute() {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isAuthenticated && user?.role) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  return <Outlet />;
}
